import { useCallback, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Video } from "@/features/studio-ai/types/studio-ai.types"

import { deleteJob, downloadVideo } from "@/api/studio-ai-api"
import { getApiErrorMessage } from "@/lib/api-error-message"

export function useVideoPlayback(options?: { onRefresh?: () => void }) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.gallery" })
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null)
  const [isPlayerOpen, setIsPlayerOpen] = useState(false)
  const [videoBlob, setVideoBlob] = useState<string | null>(null)
  const [isLoadingVideo, setIsLoadingVideo] = useState(false)
  const videoRequestIdRef = useRef(0)

  const handlePlay = useCallback(
    async (video: Video) => {
      if (!video.job_id) return
      const requestId = ++videoRequestIdRef.current
      try {
        setIsLoadingVideo(true)
        setSelectedVideo(video)
        setIsPlayerOpen(true)
        if (videoBlob) {
          URL.revokeObjectURL(videoBlob)
          setVideoBlob(null)
        }
        const blob = await downloadVideo(video.job_id)
        const nextUrl = URL.createObjectURL(blob)
        if (requestId !== videoRequestIdRef.current) {
          URL.revokeObjectURL(nextUrl)
          return
        }
        setVideoBlob(nextUrl)
      } catch {
        // downloadVideo only says "Failed to download video", in English,
        // which the translated title already covers.
        toast.error(t("failedToLoadVideo"))
        setIsPlayerOpen(false)
      } finally {
        setIsLoadingVideo(false)
      }
    },
    [t, videoBlob]
  )

  const handleClosePlayer = useCallback(() => {
    videoRequestIdRef.current += 1
    setIsPlayerOpen(false)
    setSelectedVideo(null)
    setIsLoadingVideo(false)
    if (videoBlob) {
      URL.revokeObjectURL(videoBlob)
      setVideoBlob(null)
    }
  }, [videoBlob])

  const handlePlayerOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setIsPlayerOpen(true)
        return
      }
      handleClosePlayer()
    },
    [handleClosePlayer]
  )

  const handleDownloadVideo = useCallback(
    async (video: Video) => {
      if (!video.job_id) return
      try {
        toast.info(t("downloadingVideo"))
        const blob = await downloadVideo(video.job_id)
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = `studio-ai-${video.job_id}.mp4`
        link.click()
        URL.revokeObjectURL(url)
        toast.success(t("videoDownloaded"))
      } catch {
        // Same English-only downloadVideo message as in handlePlay.
        toast.error(t("failedToDownloadVideo"))
      }
    },
    [t]
  )

  const handleDeleteVideo = useCallback(
    async (video: Video) => {
      try {
        await deleteJob(video.job_id)
        toast.success(t("videoDeleted"))
        handleClosePlayer()
        options?.onRefresh?.()
      } catch (error) {
        toast.error(t("failedToDeleteVideo"), {
          description: getApiErrorMessage(error) || undefined,
        })
      }
    },
    [t, handleClosePlayer, options]
  )

  return {
    selectedVideo,
    isPlayerOpen,
    videoBlob,
    isLoadingVideo,
    handlePlay,
    handleClosePlayer,
    handlePlayerOpenChange,
    handleDownloadVideo,
    handleDeleteVideo,
  }
}
